import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/components/ui/table'
import { useDeviceType } from '@/hooks/useDeviceType'
import { cn } from '@/lib/utils'

export interface ResponsiveTableColumn<T> {
	key: string
	header: string
	/** Rendered inside the desktop <TableCell> and used as the card value on mobile. */
	render: (row: T) => React.ReactNode
	className?: string
	/** Card mode only: hides this column's label/value from the card body — use for the title field, already shown in the card header. */
	hideOnCard?: boolean
}

interface ResponsiveTableProps<T> {
	columns: ResponsiveTableColumn<T>[]
	rows: T[]
	rowKey: (row: T, index: number) => string
	/** Card mode: field used as the card title (defaults to the first column). */
	cardTitleKey?: string
	/** Card mode: optional trailing element rendered top-right of each card (e.g. a status pill). */
	cardBadge?: (row: T) => React.ReactNode
	className?: string
	/** Makes each row/card clickable, e.g. to open a detail modal. */
	onRowClick?: (row: T) => void
}

export function ResponsiveTable<T>({
	columns,
	rows,
	rowKey,
	cardTitleKey,
	cardBadge,
	className,
	onRowClick,
}: ResponsiveTableProps<T>) {
	const { isMobile } = useDeviceType()

	if (isMobile) {
		const titleKey = cardTitleKey ?? columns[0]?.key
		const titleColumn = columns.find((c) => c.key === titleKey)
		const bodyColumns = columns.filter(
			(c) => c.key !== titleKey && !c.hideOnCard,
		)

		return (
			<div className="flex flex-col gap-2">
				{rows.map((row, index) => (
					<div
						key={rowKey(row, index)}
						className={cn(
							'rounded-lg border border-kaefer-light-silver bg-white p-3',
							onRowClick && 'cursor-pointer hover:bg-kaefer-light-silver/20',
						)}
						onClick={() => onRowClick?.(row)}
					>
						<div className="flex items-start justify-between gap-2">
							<span className="min-w-0 flex-1 text-sm font-semibold text-kaefer-dark-grey">
								{titleColumn?.render(row)}
							</span>
							{cardBadge?.(row)}
						</div>
						<dl className="mt-2 flex flex-col gap-1.5">
							{bodyColumns.map((column) => (
								<div key={column.key} className="flex flex-col gap-0.5">
									<dt className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
										{column.header}
									</dt>
									<dd className="text-xs text-kaefer-dark-grey">
										{column.render(row)}
									</dd>
								</div>
							))}
						</dl>
					</div>
				))}
			</div>
		)
	}

	return (
		<Table className={className}>
			<TableHeader>
				<TableRow>
					{columns.map((column) => (
						<TableHead key={column.key}>{column.header}</TableHead>
					))}
				</TableRow>
			</TableHeader>
			<TableBody>
				{rows.map((row, index) => (
					<TableRow
						key={rowKey(row, index)}
						className={cn(onRowClick && 'cursor-pointer')}
						onClick={() => onRowClick?.(row)}
					>
						{columns.map((column) => (
							<TableCell key={column.key} className={column.className}>
								{column.render(row)}
							</TableCell>
						))}
					</TableRow>
				))}
			</TableBody>
		</Table>
	)
}
